import axios from "axios";
import { mapMaterialSize, mapMaterialTypes } from "../utils/MaterialsUtils";
import { validateArrayInput } from "../utils/ApiValidationUtils";
import { Messages } from "../constants/Messages";
import { setupAPIRequestInterceptor, setupAPIResponseInterceptor } from "../utils/Authorization";
import { normalizeString } from "../utils/AlertUtils";

// Base URL configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.materiallogs.com";
const materialAPI = axios.create({
    baseURL: `${API_BASE_URL}/materials`,
});

setupAPIRequestInterceptor(materialAPI);
setupAPIResponseInterceptor(materialAPI);

/* =======================
 Inventory API Calls
 ======================= */

/**
 * Create a new inventory record or multiple records
 */
export const createInitialInventory = async (data: any[]): Promise<any> => {
    try {
        validateArrayInput(data); // Ensure input is valid
        const processedData = data.map((item) => ({
            ...item,
            color_code: item.color_code?.trim() || normalizeString(`${item.material_type_id}-${item.color_name?.trim()}`),
        }));
        const response = await materialAPI.post("/records", processedData); // Send the API request
        return response.data; // Return response data on success
    } catch (error: any) {

        // Extract detailed error information if available
        const errorResponse = error.response?.data;
        const errorMessage = errorResponse?.message || Messages.ERROR_CREATE;
        const errors = errorResponse?.errors || []; // Include specific errors if present

        // Throw a structured error object
        throw {
            message: errorMessage,
            errors, // Pass specific error details
        };
    }
};

/**
 * Update existing inventory records
 */
export const updateInventory = async (data: any): Promise<any> => {
    try {
        validateArrayInput(data);
        const response = await materialAPI.put("/records", data);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE);
    }
};

/**
 * Fetch all inventory records
 */
export const fetchInventoryRecords = async (): Promise<any> => {
    try {
        const response = await materialAPI.get("/records");
        return response.data.records;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH);
    }
};

/**
 * Fetch inventory history with filters
 */
export const fetchInventoryHistory = async (filters: any): Promise<any> => {
    try {
        const response = await materialAPI.get("/history", { params: filters });
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH_HISTORY);
    }
};

/* =======================
 Material Types API Calls
 ======================= */

/**
 * Fetch all material types
 */
export const fetchMaterialTypes = async (): Promise<any> => {
    try {
        const response = await materialAPI.get("/types");
        return response.data.results;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_LOAD_TYPES);
    }
};

/**
 * Create new material types
 */
export const createMaterialTypes = async (materialTypes: string[]): Promise<any> => {
    try {
        validateArrayInput(materialTypes);
        const response = await materialAPI.post("/types", mapMaterialTypes(materialTypes));
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_CREATE_SIZES);
    }
};

/**
 * Update an existing material type
 */
export const updateMaterialType = async (id: number, newType: string): Promise<any> => {
    try {
        if (!id || !newType) throw new Error(Messages.ERROR_ALL_FIELDS_REQUIRED);
        const response = await materialAPI.put(`/types/${id}`, { material_type: newType });
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_MATERIAL + " type");
    }
};

/* =======================
 Material Sizes API Calls
 ======================= */

/**
 * Fetch all material sizes
 */
export const fetchMaterialSizes = async (): Promise<any> => {
    try {
        const response = await materialAPI.get("/sizes");
        return response.data.results;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_LOAD_SIZES);
    }
};

/**
 * Create new material sizes
 */
export const createMaterialSizes = async (sizes: string[]): Promise<any> => {
    try {
        validateArrayInput(sizes);
        const response = await materialAPI.post("/sizes", mapMaterialSize(sizes));
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_CREATE_SIZES);
    }
};

/**
 * Update an existing material size
 */
export const updateMaterialSize = async (id: number, newSize: string): Promise<any> => {
    try {
        if (!id || !newSize) throw new Error(Messages.ERROR_ALL_FIELDS_REQUIRED);
        const response = await materialAPI.put(`/sizes/${id}`, { size: newSize });
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_MATERIAL + " size");
    }
};

/**
 * Delete material record by id
 */
export const deleteMaterialRecord = async (id: number): Promise<any> => {
    try {
        const response = await materialAPI.delete(`/records/${id}`);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_DELETE_RECORD);
    }
};

/**
 * Update material record by id
 */
export const updateMaterialRecord = async (id: number, data: any): Promise<any> => {
    try {
        const response = await materialAPI.put(`/records/${id}`, data);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_RECORD);
    }
};


export default materialAPI;
